import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, List, Tag, Typography, Alert, Spin, Button, Modal, message, Avatar, Progress, Space } from 'antd';
import { 
  ClockCircleOutlined, 
  ProjectOutlined, 
  TeamOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  CalendarOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const ProjectManagerDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/dashboard/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTimesheetAction = async (timesheetId, status) => {
    try {
      setActionLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/timesheet/${timesheetId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          managerId: user.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        message.success(`Timesheet ${status} successfully`);
        fetchDashboardData(); // Refresh data
      } else {
        message.error(data.message);
      }
    } catch (err) {
      message.error('Failed to update timesheet status');
      console.error('Action error:', err);
    } finally {
      setActionLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  const { manager, allProjects, projectStats, teamPerformance, recentTimesheets, summary } = dashboardData || {};
  const pendingTimesheets = recentTimesheets?.filter(ts => ts.status === 'submitted') || [];

  return (
    <div className="p-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border mb-6">
        <Title level={2} className="mb-2">Welcome back, {manager?.name}!</Title>
        <Text className="text-gray-600">
          {manager?.position} • {manager?.department}
        </Text>
        <div className="mt-2">
          <Tag color="purple">Project Manager</Tag>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Main Content - 70% */}
        <Col xs={24} lg={17}>
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card title="Quick Actions" className="shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button 
                  type="primary" 
                  icon={<CheckOutlined />} 
                  size="large"
                  onClick={() => navigate('/project-manager/approvals')}
                >
                  Review Approvals ({summary?.pendingApprovals || 0})
                </Button>
                <Button 
                  icon={<TeamOutlined />} 
                  size="large"
                  onClick={() => navigate('/project-manager/team')}
                >
                  Team Overview
                </Button>
                <Button 
                  icon={<BarChartOutlined />} 
                  size="large"
                  onClick={() => navigate('/project-manager/history')}
                >
                  View Reports
                </Button>
              </div>
            </Card>

            {/* Pending Approvals Alert */}
            {pendingTimesheets.length > 0 && (
              <Alert
                message="Action Required"
                description={`You have ${pendingTimesheets.length} timesheet entries waiting for approval.`}
                type="warning"
                showIcon
                action={
                  <Button 
                    size="small" 
                    type="primary"
                    onClick={() => navigate('/project-manager/approvals')}
                  >
                    Review Now
                  </Button>
                }
              />
            )}

            {/* Your Projects */}
            <Card title="Your Assigned Projects" className="shadow-sm">
              <div className="space-y-4">
                {allProjects?.slice(0, 4).map((project, index) => {
                  const projectStat = projectStats?.find(stat => stat.projectName === project.name);
                  return (
                    <div key={project._id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <Title level={5} className="mb-1">{project.name}</Title>
                          <Text className="text-gray-600">{project.client}</Text>
                          <div className="mt-2 flex items-center space-x-4">
                            <Tag color={project.status === 'active' ? 'green' : 'blue'}>
                              {project.status.toUpperCase()}
                            </Tag>
                            <div className="flex items-center space-x-1 text-sm text-gray-500">
                              <TeamOutlined />
                              <span>{project.teamMembers?.length || 0} members</span>
                            </div>
                            {projectStat && (
                              <div className="flex items-center space-x-1 text-sm text-gray-500">
                                <ClockCircleOutlined />
                                <span>{projectStat.totalHours}h logged</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Text className="text-sm text-gray-500">
                            {project.budget ? `$${project.budget.toLocaleString()}` : 'No budget'}
                          </Text>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!allProjects || allProjects.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <ProjectOutlined className="text-4xl mb-2" />
                    <div>No projects assigned yet</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Recent Team Activity */}
            <Card title="Recent Team Activity" className="shadow-sm">
              <List
                dataSource={Array.isArray(recentTimesheets) ? recentTimesheets.slice(0, 6) : []}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={`${item.employeeName} - ${item.hours}h`}
                      description={`${item.projectName} • ${dayjs(item.date).format('MMM DD, YYYY')} • ${item.description}`}
                    />
                    <Tag color={item.status === 'approved' ? 'green' : item.status === 'submitted' ? 'blue' : 'orange'}>
                      {item.status.toUpperCase()}
                    </Tag>
                  </List.Item>
                )}
              />
              {(!recentTimesheets || recentTimesheets.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <ClockCircleOutlined className="text-4xl mb-2" />
                  <div>No recent team activity</div>
                </div>
              )}
            </Card>
          </div>
        </Col>

        {/* Sidebar - 30% */}
        <Col xs={24} lg={7}>
          <div className="space-y-6">
            {/* Summary Statistics */}
            <Card title="Overview" className="shadow-sm">
              <div className="space-y-4">
                <Statistic
                  title="Total Projects"
                  value={summary?.totalProjects || 0}
                  prefix={<ProjectOutlined className="text-blue-500" />}
                />
                <Statistic
                  title="Team Members"
                  value={summary?.totalEmployees || 0}
                  prefix={<TeamOutlined className="text-green-500" />}
                />
                <Statistic
                  title="Hours This Month"
                  value={summary?.totalHoursThisMonth.toFixed(2) || 0}
                  suffix="h"
                  prefix={<ClockCircleOutlined className="text-orange-500" />}
                />
                <Statistic
                  title="Pending Approvals"
                  value={summary?.pendingApprovals || 0}
                  prefix={<ExclamationCircleOutlined className="text-red-500" />}
                />
              </div>
            </Card>

            {/* Project Performance */}
            <Card title="Project Performance" className="shadow-sm">
              <div className="space-y-4">
                {projectStats?.slice(0, 3).map((stat, index) => (
                  <div key={stat.projectId}>
                    <div className="flex justify-between items-center mb-2">
                      <Text strong className="text-sm">{stat.projectName}</Text>
                      <Text className="text-sm">{stat.totalHours}h</Text>
                    </div>
                    <Progress 
                      percent={Math.min((stat.totalHours / (projectStats[0]?.totalHours || 1)) * 100, 100)} 
                      size="small"
                      showInfo={false}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{stat.uniqueEmployees} members</span>
                      <span>{stat.totalEntries} entries</span>
                    </div>
                  </div>
                ))}
                {(!projectStats || projectStats.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <div className="text-sm">No project data available</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Team Insights */}
            <Card title="Team Insights" className="shadow-sm">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Text>Top Performer</Text>
                  <Text strong className="text-sm">
                    {teamPerformance?.[0]?.employeeName || 'None'}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Avg Hours/Member</Text>
                  <Text strong>
                    {teamPerformance?.length > 0 
                      ? (teamPerformance.reduce((sum, member) => sum + member.totalHours, 0) / teamPerformance.length).toFixed(1)
                      : '0'
                    }h
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Most Active Project</Text>
                  <Text strong className="text-sm">
                    {projectStats?.[0]?.projectName || 'None'}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Last Activity</Text>
                  <Text strong className="text-sm">
                    {recentTimesheets?.[0] ? dayjs(recentTimesheets[0].date).fromNow() : 'None'}
                  </Text>
                </div>
              </div>
            </Card>

            {/* Quick Stats */}
            <Card title="This Month" className="shadow-sm">
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {summary?.totalHoursThisMonth.toFixed(2) || 0}h
                  </div>
                  <Text className="text-sm text-gray-500">Total Hours Logged</Text>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold">{summary?.totalProjects || 0}</div>
                    <Text className="text-xs text-gray-500">Active Projects</Text>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold">{summary?.totalEmployees || 0}</div>
                    <Text className="text-xs text-gray-500">Team Members</Text>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectManagerDashboard;
